import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { formatDate } from '../../lib/utils';
import {
    Building2,
    Mail,
    Coins,
    Calendar,
    Edit2,
    Trash2,
    Save,
    Loader2,
    TicketCheck,
    Shield,
    ShieldOff,
    Search,
    X,
    AlertTriangle,
    User,
    Plus,
    CheckCircle,
    XCircle,
    Phone,
    Globe,
    MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' }
];

const BUSINESS_TYPES = [
    { value: 'tour_operator', label: 'Tour Operator' },
    { value: 'dmc', label: 'DMC' },
    { value: 'ota', label: 'OTA' },
    { value: 'hotel', label: 'Hotel / Resort' },
    { value: 'museum', label: 'Museum / Gallery' },
    { value: 'concierge', label: 'Concierge' },
    { value: 'other', label: 'Other' },
];

const STATUS_BADGE = {
    active: 'bg-emerald-500/10 text-emerald-400',
    pending_approval: 'bg-amber-500/10 text-amber-400',
    rejected: 'bg-red-500/10 text-red-400',
};

export default function AdminOperators() {
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [creditsOpen, setCreditsOpen] = useState(false);
    const [codesOpen, setCodesOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [selectedOp, setSelectedOp] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [creditAmount, setCreditAmount] = useState(10);
    const [creditNote, setCreditNote] = useState('');
    const [addingCredits, setAddingCredits] = useState(false);
    const [operatorCodes, setOperatorCodes] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ company_name: '', email: '', password: '', phone: '', business_type: 'tour_operator', country: '', website: '', credits: 10 });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchOperators();
    }, []);

    const fetchOperators = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/operators`, { withCredentials: true });
            setOperators(response.data);
        } catch (error) {
            toast.error('Failed to fetch operators');
        } finally {
            setLoading(false);
        }
    };

    const openEdit = (op) => {
        setSelectedOp(op);
        setEditForm({
            company_name: op.company_name || '',
            name: op.user_name || op.company_name || '',
            email: op.email || '',
            membership_plan: op.membership_plan || 'starter',
            credits: op.credits || 0,
            is_active: op.is_active !== false,
            is_whitelabel: op.is_whitelabel || false,
            features: op.features || { ar_enabled: false, ai_enabled: false }
        });
        setEditOpen(true);
    };

    const handleSave = async () => {
        if (!selectedOp) return;
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/operators/${selectedOp.id}`, editForm, { withCredentials: true });
            toast.success('Operator updated');
            setEditOpen(false);
            setSelectedOp(null);
            fetchOperators();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update operator');
        } finally {
            setSaving(false);
        }
    };

    const openDelete = (op) => {
        setSelectedOp(op);
        setDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedOp) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/api/admin/operators/${selectedOp.id}`, { withCredentials: true });
            toast.success('Operator deleted');
            setDeleteOpen(false);
            setSelectedOp(null);
            fetchOperators();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete operator');
        } finally {
            setDeleting(false);
        }
    };

    const openCreditsDialog = (op) => {
        setSelectedOp(op);
        setCreditAmount(10);
        setCreditNote('');
        setCreditsOpen(true);
    };

    const handleAddCredits = async () => {
        if (!selectedOp || creditAmount <= 0) return;
        setAddingCredits(true);
        try {
            const res = await axios.post(`${API}/api/admin/operators/${selectedOp.id}/add-credits`, {
                amount: creditAmount,
                note: creditNote
            }, { withCredentials: true });
            toast.success(`Added ${creditAmount} credits. New balance: ${res.data.credits}`);
            setCreditsOpen(false);
            setSelectedOp(null);
            fetchOperators();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add credits');
        } finally {
            setAddingCredits(false);
        }
    };

    const openCodesDialog = async (op) => {
        setSelectedOp(op);
        setOperatorCodes([]);
        setCodesOpen(true);
        try {
            const res = await axios.get(`${API}/api/admin/codes?operator_id=${op.id}`, { withCredentials: true });
            setOperatorCodes(res.data);
        } catch (error) {
            toast.error('Failed to fetch operator codes');
        }
    };

    const handleCreateOperator = async () => {
        if (!createForm.company_name || !createForm.email || !createForm.password) {
            toast.error('Company name, email, and password are required');
            return;
        }
        setCreating(true);
        try {
            await axios.post(`${API}/api/admin/operators/create`, createForm, { withCredentials: true });
            toast.success('Operator created successfully');
            setCreateOpen(false);
            setCreateForm({ company_name: '', email: '', password: '', phone: '', business_type: 'tour_operator', country: '', website: '', credits: 10 });
            fetchOperators();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create operator');
        } finally {
            setCreating(false);
        }
    };

    const handleApprove = async (opId) => {
        try {
            await axios.put(`${API}/api/admin/operators/${opId}/approve`, {}, { withCredentials: true });
            toast.success('Operator approved');
            fetchOperators();
        } catch (error) {
            toast.error('Failed to approve operator');
        }
    };

    const handleReject = async (opId) => {
        try {
            await axios.put(`${API}/api/admin/operators/${opId}/reject`, {}, { withCredentials: true });
            toast.success('Operator rejected');
            fetchOperators();
        } catch (error) {
            toast.error('Failed to reject operator');
        }
    };

    const filtered = operators.filter(op => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (op.company_name || '').toLowerCase().includes(q) ||
               (op.email || '').toLowerCase().includes(q) ||
               (op.user_name || '').toLowerCase().includes(q);
    });

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-operators">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Tour Operators</h1>
                        <p className="text-muted-foreground mt-1">Manage partner tour operators ({operators.length} total)</p>
                    </div>
                    <Button onClick={() => setCreateOpen(true)} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="create-operator-btn">
                        <Plus className="w-4 h-4" /> Create Operator
                    </Button>
                </div>

                {/* Search */}
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by name or email..."
                        className="pl-10 bg-[#121620] border-white/10"
                        data-testid="operator-search-input"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                            <X className="w-4 h-4 text-muted-foreground hover:text-white" />
                        </button>
                    )}
                </div>

                {/* Operators Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Operator</TableHead>
                                    <TableHead className="text-muted-foreground">Email</TableHead>
                                    <TableHead className="text-muted-foreground">Plan</TableHead>
                                    <TableHead className="text-muted-foreground">Credits</TableHead>
                                    <TableHead className="text-muted-foreground">Codes</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Joined</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((operator) => (
                                    <TableRow key={operator.id} className="border-white/10 hover:bg-white/5">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-md bg-gold/10 flex items-center justify-center">
                                                    <Building2 className="w-5 h-5 text-gold" />
                                                </div>
                                                <div>
                                                    <p className="text-white font-medium" data-testid={`op-name-${operator.id}`}>{operator.company_name}</p>
                                                    <p className="text-xs text-muted-foreground">{operator.user_name}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-muted-foreground text-sm">
                                                <Mail className="w-3 h-3" />
                                                <span>{operator.email || '—'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                                                operator.membership_plan === 'enterprise' ? 'bg-gold/10 text-gold' :
                                                operator.membership_plan === 'professional' ? 'bg-blue-500/10 text-blue-400' :
                                                'bg-white/10 text-muted-foreground'
                                            }`}>
                                                {operator.membership_plan}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-gold">
                                                <Coins className="w-4 h-4" />
                                                <span>{operator.credits || 0}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <TicketCheck className="w-4 h-4" />
                                                <span>{operator.code_count || 0}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_BADGE[operator.status] || STATUS_BADGE.active}`}>
                                                    {(operator.status || 'active').replace('_', ' ')}
                                                </span>
                                                {operator.is_whitelabel && (
                                                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-gold/10 text-gold border border-gold/20 font-medium">WL</span>
                                                )}
                                                {operator.status === 'pending_approval' && (
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => handleApprove(operator.id)} className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400" title="Approve" data-testid={`approve-${operator.id}`}>
                                                            <CheckCircle className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleReject(operator.id)} className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Reject" data-testid={`reject-${operator.id}`}>
                                                            <XCircle className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-muted-foreground text-sm">
                                                <Calendar className="w-3 h-3" />
                                                <span>{formatDate(operator.created_at)}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center gap-1 justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openCreditsDialog(operator)}
                                                    className="text-gold hover:text-gold/80 hover:bg-gold/10 gap-1 text-xs"
                                                    data-testid={`add-credits-${operator.id}`}
                                                >
                                                    <Coins className="w-3.5 h-3.5" />
                                                    <span className="hidden xl:inline">Credits</span>
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openCodesDialog(operator)}
                                                    className="text-muted-foreground hover:text-white gap-1 text-xs"
                                                    data-testid={`view-codes-${operator.id}`}
                                                >
                                                    <TicketCheck className="w-3.5 h-3.5" />
                                                    <span className="hidden xl:inline">Codes</span>
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(operator)}
                                                    className="text-muted-foreground hover:text-white"
                                                    data-testid={`edit-op-${operator.id}`}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openDelete(operator)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    data-testid={`delete-op-${operator.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                            {searchQuery ? 'No operators match your search.' : 'No operators found.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Edit Dialog */}
                <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); if (!open) setSelectedOp(null); }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit">Edit Operator</DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">Update operator profile, plan, and account status.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Company Name</Label>
                                <Input
                                    value={editForm.company_name}
                                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="edit-company-name"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Contact Name</Label>
                                    <Input
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="edit-contact-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={editForm.email}
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="edit-email"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Membership Plan</Label>
                                    <Select value={editForm.membership_plan} onValueChange={(v) => setEditForm({ ...editForm, membership_plan: v })}>
                                        <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="edit-plan">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#121620] border-white/10">
                                            {PLANS.map(p => (
                                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Credits</Label>
                                    <Input
                                        type="number"
                                        value={editForm.credits}
                                        onChange={(e) => setEditForm({ ...editForm, credits: parseInt(e.target.value) || 0 })}
                                        min={0}
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="edit-credits"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md bg-[#0A0D14] border border-white/10">
                                <div className="flex items-center gap-2">
                                    {editForm.is_active ? (
                                        <Shield className="w-4 h-4 text-emerald-400" />
                                    ) : (
                                        <ShieldOff className="w-4 h-4 text-red-400" />
                                    )}
                                    <span className="text-sm text-white">
                                        {editForm.is_active ? 'Account Active' : 'Account Suspended'}
                                    </span>
                                </div>
                                <Switch
                                    checked={editForm.is_active}
                                    onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })}
                                    data-testid="edit-active-switch"
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md bg-gold/5 border border-gold/20">
                                <div>
                                    <p className="text-sm text-white font-medium">White-Label Mode</p>
                                    <p className="text-xs text-muted-foreground">Custom branding, exclusive content, discounted credits</p>
                                </div>
                                <Switch
                                    checked={editForm.is_whitelabel || false}
                                    onCheckedChange={(v) => setEditForm({ ...editForm, is_whitelabel: v })}
                                    data-testid="edit-whitelabel-switch"
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md bg-[#0A0D14] border border-white/5">
                                <div>
                                    <p className="text-sm text-white font-medium">AR Experience</p>
                                    <p className="text-xs text-muted-foreground">Historical overlays at attractions</p>
                                </div>
                                <Switch
                                    checked={editForm.features?.ar_enabled || false}
                                    onCheckedChange={(v) => setEditForm({ ...editForm, features: { ...(editForm.features || {}), ar_enabled: v } })}
                                    data-testid="edit-ar-switch"
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-md bg-[#0A0D14] border border-white/5">
                                <div>
                                    <p className="text-sm text-white font-medium">AI Assistant</p>
                                    <p className="text-xs text-muted-foreground">GPT-5.2 chatbot for travelers</p>
                                </div>
                                <Switch
                                    checked={editForm.features?.ai_enabled || false}
                                    onCheckedChange={(v) => setEditForm({ ...editForm, features: { ...(editForm.features || {}), ai_enabled: v } })}
                                    data-testid="edit-ai-switch"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button variant="outline" onClick={() => setEditOpen(false)} className="border-white/10">
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} disabled={saving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-operator-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteOpen} onOpenChange={(open) => { setDeleteOpen(open); if (!open) setSelectedOp(null); }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-400" />
                                Delete Operator
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">This action cannot be undone.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <p className="text-muted-foreground">
                                Are you sure you want to permanently delete <span className="text-white font-medium">{selectedOp?.company_name}</span>?
                            </p>
                            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/20 text-sm text-red-300 space-y-1">
                                <p>This will:</p>
                                <ul className="list-disc list-inside space-y-0.5 text-red-300/80">
                                    <li>Delete the operator profile</li>
                                    <li>Delete their user account</li>
                                    <li>Deactivate all their redeem codes</li>
                                </ul>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="outline" onClick={() => setDeleteOpen(false)} className="border-white/10">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="bg-red-600 hover:bg-red-700 text-white gap-2"
                                    data-testid="confirm-delete-btn"
                                >
                                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    Delete Permanently
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Add Credits Dialog */}
                <Dialog open={creditsOpen} onOpenChange={(open) => { setCreditsOpen(open); if (!open) setSelectedOp(null); }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <Coins className="w-5 h-5 text-gold" />
                                Add Credits
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                Add credits to <span className="text-white">{selectedOp?.company_name}</span>. 1 credit = 1 attraction on 1 device.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div className="p-3 rounded-md bg-[#0A0D14] border border-white/10 flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Current Balance</span>
                                <span className="text-gold font-semibold">{selectedOp?.credits || 0} credits</span>
                            </div>
                            <div className="space-y-2">
                                <Label>Amount to Add</Label>
                                <Input
                                    type="number"
                                    value={creditAmount}
                                    onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                                    min={1}
                                    max={10000}
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="credit-amount-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Note (optional)</Label>
                                <Input
                                    value={creditNote}
                                    onChange={(e) => setCreditNote(e.target.value)}
                                    placeholder="e.g. Summer 2026 package"
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="credit-note-input"
                                />
                            </div>
                            <div className="p-3 rounded-md bg-gold/5 border border-gold/20 flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">New Balance</span>
                                <span className="text-gold font-semibold">{(selectedOp?.credits || 0) + creditAmount} credits</span>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="outline" onClick={() => setCreditsOpen(false)} className="border-white/10">Cancel</Button>
                                <Button
                                    onClick={handleAddCredits}
                                    disabled={addingCredits || creditAmount <= 0}
                                    className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                                    data-testid="confirm-add-credits-btn"
                                >
                                    {addingCredits ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
                                    Add {creditAmount} Credits
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* View Operator Codes Dialog */}
                <Dialog open={codesOpen} onOpenChange={(open) => { setCodesOpen(open); if (!open) { setSelectedOp(null); setOperatorCodes([]); } }}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <TicketCheck className="w-5 h-5 text-gold" />
                                Codes — {selectedOp?.company_name}
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                {operatorCodes.length} code{operatorCodes.length !== 1 ? 's' : ''} generated by this operator
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-2">
                            {operatorCodes.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <TicketCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                    <p>No codes generated yet</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-white/10 hover:bg-transparent">
                                            <TableHead className="text-muted-foreground">Code</TableHead>
                                            <TableHead className="text-muted-foreground">Attractions</TableHead>
                                            <TableHead className="text-muted-foreground">Devices</TableHead>
                                            <TableHead className="text-muted-foreground">Status</TableHead>
                                            <TableHead className="text-muted-foreground">Created</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {operatorCodes.map((code) => (
                                            <TableRow key={code.id} className="border-white/10 hover:bg-white/5">
                                                <TableCell>
                                                    <code className="font-mono text-white text-sm">{code.code}</code>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm">
                                                    {code.attraction_ids?.length || 0} attraction{(code.attraction_ids?.length || 0) !== 1 ? 's' : ''}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm">
                                                    {code.current_uses}/{code.max_uses}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                                        code.current_uses >= code.max_uses ? 'bg-red-500/10 text-red-400' :
                                                        code.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'
                                                    }`}>
                                                        {code.current_uses >= code.max_uses ? 'Used' : code.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-xs">{formatDate(code.created_at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Create Operator Dialog */}
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit">Create New Operator</DialogTitle>
                            <DialogDescription>Manually create a partner operator account</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label className="text-white/70">Company Name *</Label>
                                <Input value={createForm.company_name} onChange={e => setCreateForm(p => ({...p, company_name: e.target.value}))} placeholder="Company name" className="bg-[#0A0D14] border-white/10" data-testid="create-company-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Email *</Label>
                                    <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({...p, email: e.target.value}))} placeholder="work@company.com" className="bg-[#0A0D14] border-white/10" data-testid="create-email" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Password *</Label>
                                    <Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({...p, password: e.target.value}))} placeholder="Min 8 characters" className="bg-[#0A0D14] border-white/10" data-testid="create-password" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Phone</Label>
                                    <Input value={createForm.phone} onChange={e => setCreateForm(p => ({...p, phone: e.target.value}))} placeholder="+1 234 567" className="bg-[#0A0D14] border-white/10" data-testid="create-phone" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Business Type</Label>
                                    <select value={createForm.business_type} onChange={e => setCreateForm(p => ({...p, business_type: e.target.value}))} className="w-full h-10 rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 text-sm" data-testid="create-business-type">
                                        {BUSINESS_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Country</Label>
                                    <Input value={createForm.country} onChange={e => setCreateForm(p => ({...p, country: e.target.value}))} placeholder="Italy" className="bg-[#0A0D14] border-white/10" data-testid="create-country" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Website</Label>
                                    <Input value={createForm.website} onChange={e => setCreateForm(p => ({...p, website: e.target.value}))} placeholder="https://..." className="bg-[#0A0D14] border-white/10" data-testid="create-website" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Starting Credits</Label>
                                <Input type="number" value={createForm.credits} onChange={e => setCreateForm(p => ({...p, credits: parseInt(e.target.value) || 0}))} className="bg-[#0A0D14] border-white/10 w-32" data-testid="create-credits" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button onClick={handleCreateOperator} disabled={creating} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="create-submit-btn">
                                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    Create Operator
                                </Button>
                                <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/20 text-white/70">Cancel</Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
